import React, { useState, useEffect } from 'react'
import {
  Moon,
  BatteryCharging,
  HeartPulse,
  AlertTriangle,
  Stethoscope,
  Save,
  Send,
  WifiOff,
  History,
  CheckCircle2,
  PlusCircle,
} from 'lucide-react'
import { Modal } from '../shared/Modal'
import { formsService } from './formsService'
import {
  CampDailyCheckinPayload,
  FormResponseRecord,
  HealthIncidentItem,
  SLEEP_QUALITY_LABELS,
  FATIGUE_LABELS,
  RECOVERY_LABELS,
  DISCOMFORT_STATUS_LABELS,
  DiscomfortStatus,
} from '../../types/health'

interface CampDailyCheckinModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (status: 'draft' | 'submitted') => void
  teamId: string
  athleteId: string
  eventId: string
  eventTitle?: string
  campName?: string
  targetDate: string // YYYY-MM-DD
  dueTime?: string // HH:MM:SS, default '09:00:00'
  initialResponse?: FormResponseRecord | null
  existingResponse?: FormResponseRecord | null
  openEpisodes?: HealthIncidentItem[]
  onOpenIncidentModal?: () => void
}

export const CampDailyCheckinModal: React.FC<CampDailyCheckinModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  teamId,
  athleteId,
  eventId,
  eventTitle,
  campName,
  targetDate,
  dueTime = '09:00:00',
  initialResponse,
  existingResponse,
  openEpisodes = [],
  onOpenIncidentModal,
}) => {
  const effectiveResponse = initialResponse || existingResponse || null
  const effectiveEventTitle = eventTitle || campName || 'Concentración'

  // Estado de conexión a internet (requisito: estrictamente online, no indexedDB para salud)
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  )

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Cargar estado inicial desde effectiveResponse (si existe borrador o respuesta enviada)
  const rawPayload = effectiveResponse?.payload as CampDailyCheckinPayload | undefined

  // Horas de sueño: default a valor existente o 7.5h estándar de referencia
  const [sleepHours, setSleepHours] = useState<number>(rawPayload?.sleep_hours ?? 7.5)

  // Calidad, fatiga y recuperación: SIN selección predeterminada que se confunda con
  // respuesta del atleta; los valores existentes (borrador/enviado) sí se recuperan.
  const [sleepQuality, setSleepQuality] = useState<number | null>(
    rawPayload?.sleep_quality !== undefined ? Number(rawPayload.sleep_quality) : null
  )
  const [fatigue, setFatigue] = useState<number | null>(
    rawPayload?.fatigue !== undefined ? Number(rawPayload.fatigue) : null
  )
  const [recovery, setRecovery] = useState<number | null>(
    rawPayload?.recovery !== undefined ? Number(rawPayload.recovery) : null
  )
  const [discomfortStatus, setDiscomfortStatus] = useState<DiscomfortStatus | null>(
    rawPayload?.discomfort_status ?? null
  )
  const [relatedIncidentId, setRelatedIncidentId] = useState<string>(
    rawPayload?.related_incident_id ?? ''
  )
  const [discomfortNotes, setDiscomfortNotes] = useState<string>(
    rawPayload?.discomfort_notes ?? ''
  )
  const [contactRequested, setContactRequested] = useState<boolean>(
    rawPayload?.contact_requested ?? false
  )
  const [contactComment, setContactComment] = useState<string>(
    rawPayload?.contact_comment ?? ''
  )

  // Estados de interfaz y guardado
  const [isSavingDraft, setIsSavingDraft] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [feedbackMsg, setFeedbackMsg] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(
    null
  )
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // Re-sincronizar si cambia effectiveResponse
  useEffect(() => {
    if (effectiveResponse?.payload) {
      const p = effectiveResponse.payload as CampDailyCheckinPayload
      setSleepHours(p.sleep_hours ?? 7.5)
      setSleepQuality(p.sleep_quality !== undefined ? Number(p.sleep_quality) : null)
      setFatigue(p.fatigue !== undefined ? Number(p.fatigue) : null)
      setRecovery(p.recovery !== undefined ? Number(p.recovery) : null)
      setDiscomfortStatus(p.discomfort_status ?? null)
      setRelatedIncidentId(p.related_incident_id ?? '')
      setDiscomfortNotes(p.discomfort_notes ?? '')
      setContactRequested(p.contact_requested ?? false)
      setContactComment(p.contact_comment ?? '')
      setHasUnsavedChanges(false)
    }
  }, [effectiveResponse])

  // Comprobar si ya fue enviado previamente
  const isPreviouslySubmitted = effectiveResponse?.status === 'submitted'
  const revisionNumber = effectiveResponse?.revision ?? effectiveResponse?.revision_number ?? 1

  const handleFieldChange = () => {
    setHasUnsavedChanges(true)
    setFeedbackMsg(null)
  }

  const handleSave = async (status: 'draft' | 'submitted') => {
    if (!isOnline) {
      setFeedbackMsg({
        type: 'error',
        text: 'Se requiere conexión a internet para registrar datos sanitarios.',
      })
      return
    }

    // Validación de horas de sueño
    if (isNaN(sleepHours) || sleepHours < 0 || sleepHours > 24) {
      setFeedbackMsg({
        type: 'error',
        text: 'Las horas de sueño deben estar comprendidas entre 0 y 24 horas.',
      })
      return
    }

    // Validación obligatoria para ENVÍO DEFINITIVO
    if (status === 'submitted') {
      const missingFields: string[] = []
      if (sleepQuality === null) missingFields.push('Calidad del sueño')
      if (fatigue === null) missingFields.push('Fatiga percibida')
      if (recovery === null) missingFields.push('Recuperación percibida')
      if (discomfortStatus === null) missingFields.push('Molestias musculares')

      if (missingFields.length > 0) {
        setFeedbackMsg({
          type: 'error',
          text: `Por favor, responde a todas las preguntas obligatorias antes de enviar: ${missingFields.join(', ')}.`,
        })
        return
      }
    }

    if (status === 'draft') {
      setIsSavingDraft(true)
    } else {
      setIsSubmitting(true)
    }
    setFeedbackMsg(null)

    const payload: CampDailyCheckinPayload = {
      sleep_hours: Math.round(Number(sleepHours) * 10) / 10,
      sleep_quality: sleepQuality !== null ? Number(sleepQuality) : 3, // fallback técnico para borrador
      fatigue: fatigue !== null ? Number(fatigue) : 3,
      recovery: recovery !== null ? Number(recovery) : 3,
      discomfort_status: discomfortStatus || 'none',
      related_incident_id:
        discomfortStatus === 'worsened' && relatedIncidentId ? relatedIncidentId : undefined,
      discomfort_notes: discomfortNotes.trim() || undefined,
      contact_requested: Boolean(contactRequested),
      contact_comment: contactRequested ? contactComment.trim() || undefined : undefined,
    }

    try {
      const result = await formsService.saveCampDailyCheckin({
        teamId,
        athleteId,
        eventId,
        targetDate,
        status,
        payload,
      })

      if (result.success) {
        setHasUnsavedChanges(false)
        setFeedbackMsg({
          type: 'success',
          text:
            status === 'draft'
              ? 'Borrador guardado correctamente en el servidor.'
              : 'Check-in diario enviado con éxito.',
        })
        onSuccess(status)
        if (status === 'submitted') {
          setTimeout(() => {
            onClose()
          }, 1200)
        }
      } else {
        setFeedbackMsg({
          type: 'error',
          text: 'Error al guardar el check-in diario en el servidor.',
        })
      }
    } catch (err: unknown) {
      setFeedbackMsg({
        type: 'error',
        text: err instanceof Error ? err.message : 'Error inesperado al conectar con el servidor.',
      })
    } finally {
      setIsSavingDraft(false)
      setIsSubmitting(false)
    }
  }

  const formattedDate = targetDate
    ? new Date(targetDate + 'T12:00:00Z').toLocaleDateString('es-ES', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      })
    : ''

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Check-in Diario de Concentración"
      subtitle={`${effectiveEventTitle} · ${formattedDate} (Límite: ${dueTime.slice(0, 5)} h)`}
      icon={<HeartPulse size={20} style={{ color: 'var(--brand-red)' }} />}
      size="lg"
      hasUnsavedChanges={hasUnsavedChanges}
      ariaLabel="Formulario de Check-in Diario de Concentración"
      footer={
        <div className="checkin-footer-wrap">
          <div className="checkin-footer-info">
            {isPreviouslySubmitted ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontWeight: 600 }}>
                <History size={15} style={{ color: 'var(--brand-navy)' }} />
                Revisión #{revisionNumber} · Modificación auditada
              </span>
            ) : (
              <span>Completa en menos de 1 minuto</span>
            )}
          </div>
          <div className="checkin-footer-actions">
            <button
              type="button"
              onClick={onClose}
              disabled={isSavingDraft || isSubmitting}
              className="btn btn-secondary"
            >
              Cerrar
            </button>
            <button
              type="button"
              onClick={() => handleSave('draft')}
              disabled={!isOnline || isSavingDraft || isSubmitting}
              className="btn btn-secondary"
            >
              <Save size={16} />
              {isSavingDraft ? 'Guardando...' : 'Guardar borrador'}
            </button>
            <button
              type="button"
              onClick={() => handleSave('submitted')}
              disabled={!isOnline || isSavingDraft || isSubmitting}
              className="btn btn-primary"
            >
              <Send size={16} />
              {isSubmitting
                ? 'Enviando...'
                : isPreviouslySubmitted
                ? `Actualizar (Rev. #${revisionNumber + 1})`
                : 'Enviar check-in'}
            </button>
          </div>
        </div>
      }
    >
      <div className="checkin-modal-container">
        {/* Aviso de conectividad offline */}
        {!isOnline && (
          <div className="checkin-banner checkin-banner-warning">
            <WifiOff size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <strong style={{ display: 'block', marginBottom: '0.2rem' }}>Sin conexión a internet</strong>
              <span style={{ fontSize: '0.8rem' }}>
                Los datos de salud son estrictamente online por protección de privacidad. Tus cambios se mantienen en memoria mientras este diálogo esté abierto, pero debes restablecer conexión para guardar o enviar.
              </span>
            </div>
          </div>
        )}

        {/* Feedback visual de éxito o error */}
        {feedbackMsg && (
          <div
            className={`checkin-banner ${
              feedbackMsg.type === 'success'
                ? 'checkin-banner-success'
                : feedbackMsg.type === 'warning'
                ? 'checkin-banner-warning'
                : 'checkin-banner-error'
            }`}
          >
            {feedbackMsg.type === 'success' ? (
              <CheckCircle2 size={18} style={{ flexShrink: 0 }} />
            ) : (
              <AlertTriangle size={18} style={{ flexShrink: 0 }} />
            )}
            <span style={{ fontWeight: 600 }}>{feedbackMsg.text}</span>
          </div>
        )}

        {/* Histórico si ya está enviado */}
        {isPreviouslySubmitted && (
          <div className="checkin-banner checkin-banner-info">
            <History size={18} style={{ flexShrink: 0 }} />
            <div>
              <span style={{ fontWeight: 700 }}>Check-in enviado previamente (Revisión #{revisionNumber})</span>
              <span style={{ display: 'block', fontSize: '0.8rem', marginTop: '0.15rem' }}>
                Cualquier cambio guardará una nueva versión cronológica auditada.
              </span>
            </div>
          </div>
        )}

        {/* 1. HORAS Y CALIDAD DEL SUEÑO */}
        <section className="checkin-section">
          <div className="checkin-section-header">
            <Moon size={19} style={{ color: 'var(--brand-navy)' }} />
            <span>1. Sueño de la última noche</span>
          </div>

          <div className="checkin-grid-2col">
            {/* Horas de sueño */}
            <div className="checkin-field-group">
              <label htmlFor="sleep-hours-input" className="checkin-label">
                <span>Horas de sueño</span>
                <span className="checkin-label-hint">Paso de 0,5 h</span>
              </label>
              <div className="checkin-stepper-row">
                <button
                  type="button"
                  onClick={() => {
                    setSleepHours((prev) => Math.max(0, Math.round((prev - 0.5) * 10) / 10))
                    handleFieldChange()
                  }}
                  className="checkin-stepper-btn"
                  aria-label="Restar 30 minutos de sueño"
                  disabled={sleepHours <= 0}
                >
                  −
                </button>
                <div className="checkin-stepper-input-wrap">
                  <input
                    id="sleep-hours-input"
                    aria-label="Horas de sueño"
                    type="number"
                    step="0.5"
                    min="0"
                    max="24"
                    value={sleepHours}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value)
                      setSleepHours(isNaN(val) ? 0 : Math.max(0, Math.min(24, Math.round(val * 10) / 10)))
                      handleFieldChange()
                    }}
                    className="checkin-stepper-input"
                  />
                  <span className="checkin-stepper-unit">horas</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSleepHours((prev) => Math.min(24, Math.round((prev + 0.5) * 10) / 10))
                    handleFieldChange()
                  }}
                  className="checkin-stepper-btn"
                  aria-label="Sumar 30 minutos de sueño"
                  disabled={sleepHours >= 24}
                >
                  +
                </button>
              </div>
            </div>

            {/* Calidad del sueño (1-5) */}
            <div className="checkin-field-group">
              <div className="checkin-label">
                <span>Calidad del sueño (1 a 5)</span>
                <span className="checkin-label-hint">1 = mala · 5 = óptima</span>
              </div>
              <div
                className="checkin-scale-grid"
                role="radiogroup"
                aria-label="Calidad del sueño del 1 al 5"
              >
                {[1, 2, 3, 4, 5].map((val) => {
                  const isSelected = sleepQuality === val
                  return (
                    <button
                      key={val}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      aria-pressed={isSelected}
                      onClick={() => {
                        setSleepQuality(val)
                        handleFieldChange()
                      }}
                      className={`checkin-scale-btn ${isSelected ? 'selected' : ''}`}
                    >
                      <span>{val}</span>
                    </button>
                  )
                })}
              </div>
              <div className="checkin-scale-desc">
                {sleepQuality !== null ? (
                  <strong>{SLEEP_QUALITY_LABELS[sleepQuality as keyof typeof SLEEP_QUALITY_LABELS]}</strong>
                ) : (
                  <span style={{ color: 'var(--text-muted)' }}>Selecciona una opción (1 a 5)</span>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* 2. FATIGA Y RECUPERACIÓN */}
        <section className="checkin-section">
          <div className="checkin-section-header">
            <BatteryCharging size={19} style={{ color: 'var(--brand-navy)' }} />
            <span>2. Estado físico al despertar</span>
          </div>

          <div className="checkin-grid-2col">
            {/* Fatiga percibida (1-5) */}
            <div className="checkin-field-group">
              <div className="checkin-label">
                <span>Fatiga percibida</span>
                <span className="checkin-label-hint">1 = nada · 5 = extrema</span>
              </div>
              <div
                className="checkin-scale-grid"
                role="radiogroup"
                aria-label="Fatiga percibida del 1 al 5"
              >
                {[1, 2, 3, 4, 5].map((val) => {
                  const isSelected = fatigue === val
                  return (
                    <button
                      key={val}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      aria-pressed={isSelected}
                      onClick={() => {
                        setFatigue(val)
                        handleFieldChange()
                      }}
                      className={`checkin-scale-btn ${isSelected ? 'selected' : ''}`}
                    >
                      <span>{val}</span>
                    </button>
                  )
                })}
              </div>
              <div className="checkin-scale-desc">
                {fatigue !== null ? (
                  <strong>{FATIGUE_LABELS[fatigue as keyof typeof FATIGUE_LABELS]}</strong>
                ) : (
                  <span style={{ color: 'var(--text-muted)' }}>Selecciona nivel de fatiga (1 a 5)</span>
                )}
              </div>
            </div>

            {/* Recuperación percibida (1-5, independiente de fatiga) */}
            <div className="checkin-field-group">
              <div className="checkin-label">
                <span>Recuperación percibida</span>
                <span className="checkin-label-hint">1 = muy mal · 5 = total</span>
              </div>
              <div
                className="checkin-scale-grid"
                role="radiogroup"
                aria-label="Recuperación percibida del 1 al 5"
              >
                {[1, 2, 3, 4, 5].map((val) => {
                  const isSelected = recovery === val
                  return (
                    <button
                      key={val}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      aria-pressed={isSelected}
                      onClick={() => {
                        setRecovery(val)
                        handleFieldChange()
                      }}
                      className={`checkin-scale-btn ${isSelected ? 'selected' : ''}`}
                    >
                      <span>{val}</span>
                    </button>
                  )
                })}
              </div>
              <div className="checkin-scale-desc">
                {recovery !== null ? (
                  <strong>{RECOVERY_LABELS[recovery as keyof typeof RECOVERY_LABELS]}</strong>
                ) : (
                  <span style={{ color: 'var(--text-muted)' }}>Selecciona nivel de recuperación (1 a 5)</span>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* 3. MOLESTIAS Y LESIONES */}
        <section className="checkin-section">
          <div className="checkin-section-header">
            <AlertTriangle size={19} style={{ color: '#d97706' }} />
            <span>3. Molestias o dolores musculares</span>
          </div>

          <div>
            <label className="checkin-label" style={{ marginBottom: '0.65rem' }}>
              <span>¿Tienes alguna molestia nueva o ha empeorado alguna existente?</span>
            </label>
            <div
              className="checkin-radio-group"
              role="radiogroup"
              aria-label="Estado de molestias musculares"
            >
              {(['none', 'new', 'worsened'] as DiscomfortStatus[]).map((status) => {
                const isSelected = discomfortStatus === status
                return (
                  <button
                    key={status}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    aria-pressed={isSelected}
                    onClick={() => {
                      setDiscomfortStatus(status)
                      handleFieldChange()
                    }}
                    className={`checkin-radio-card ${
                      isSelected
                        ? status === 'none'
                          ? 'selected-none'
                          : 'selected-alert'
                        : ''
                    }`}
                  >
                    <span>{DISCOMFORT_STATUS_LABELS[status]}</span>
                    {isSelected && <CheckCircle2 size={18} style={{ flexShrink: 0 }} />}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Si indica molestia existente empeorada: selector de episodio accesible */}
          {discomfortStatus === 'worsened' && (
            <div className="checkin-subpanel checkin-subpanel-amber">
              <label
                htmlFor="select-episode"
                style={{
                  display: 'block',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  color: '#78350f',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                  marginBottom: '0.45rem',
                }}
              >
                Selecciona el episodio o molestia que ha empeorado
              </label>
              {openEpisodes.length > 0 ? (
                <select
                  id="select-episode"
                  value={relatedIncidentId}
                  onChange={(e) => {
                    setRelatedIncidentId(e.target.value)
                    handleFieldChange()
                  }}
                  style={{
                    width: '100%',
                    minHeight: '44px',
                    padding: '0.55rem 0.85rem',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid #fcd34d',
                    backgroundColor: '#ffffff',
                    color: 'var(--text-main)',
                    fontSize: '0.88rem',
                    fontWeight: 500,
                  }}
                >
                  <option value="">-- Seleccionar molestia abierta --</option>
                  {openEpisodes.map((ep) => (
                    <option key={ep.id} value={ep.id}>
                      {ep.title || ep.description || 'Molestia abierta'} (
                      {new Date(ep.incident_date).toLocaleDateString('es-ES')})
                    </option>
                  ))}
                </select>
              ) : (
                <p style={{ margin: 0, fontSize: '0.82rem', color: '#92400e' }}>
                  No tienes episodios previos registrados como abiertos. Si se trata de una molestia reciente, marca «Molestia nueva».
                </p>
              )}
            </div>
          )}

          {/* Si indica molestia nueva: opción de registrar incidencia formal */}
          {discomfortStatus === 'new' && (
            <div
              className="checkin-subpanel checkin-subpanel-amber"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}
            >
              <div>
                <strong style={{ display: 'block', fontSize: '0.82rem', color: '#78350f', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                  Comunicación clínica de nueva molestia
                </strong>
                <span style={{ fontSize: '0.8rem', color: '#92400e', marginTop: '0.15rem', display: 'block' }}>
                  Puedes registrar el detalle anatómico completo en el formulario de incidencias sin perder tu check-in.
                </span>
              </div>
              {onOpenIncidentModal && (
                <button
                  type="button"
                  onClick={onOpenIncidentModal}
                  className="btn btn-secondary"
                  style={{ minHeight: '40px', padding: '0.45rem 0.85rem', fontSize: '0.82rem' }}
                >
                  <PlusCircle size={15} />
                  Abrir formulario de molestia
                </button>
              )}
            </div>
          )}

          {/* Notas de molestia si no es 'none' */}
          {discomfortStatus !== null && discomfortStatus !== 'none' && (
            <div style={{ marginTop: '0.85rem' }}>
              <label
                htmlFor="discomfort-notes"
                className="checkin-label"
                style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.02em', color: 'var(--text-secondary)' }}
              >
                <span>Detalle breve de la molestia (opcional)</span>
              </label>
              <textarea
                id="discomfort-notes"
                rows={2}
                value={discomfortNotes}
                onChange={(e) => {
                  setDiscomfortNotes(e.target.value)
                  handleFieldChange()
                }}
                placeholder="Ejemplo: sobrecarga en gemelo derecho tras la sesión de la tarde..."
                style={{
                  width: '100%',
                  padding: '0.65rem 0.85rem',
                  fontSize: '0.88rem',
                  color: 'var(--text-main)',
                  backgroundColor: '#ffffff',
                  border: '1px solid var(--surface-border-strong)',
                  borderRadius: 'var(--radius-md)',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          )}
        </section>

        {/* 4. CONTACTO CON EL SERVICIO MÉDICO */}
        <section className="checkin-section">
          <div className="checkin-section-header">
            <Stethoscope size={19} style={{ color: 'var(--brand-navy)' }} />
            <span>4. Atención y contacto sanitario</span>
          </div>

          <div>
            <div style={{ marginBottom: '0.75rem' }}>
              <p style={{ margin: 0, fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-main)' }}>
                ¿Necesitas que contacte contigo el servicio médico hoy?
              </p>
              <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                Aparecerá en la bandeja médica de la concentración como solicitud prioritaria.
              </p>
            </div>

            <div
              className="checkin-toggle-group"
              role="radiogroup"
              aria-label="Solicitud de contacto médico"
            >
              <button
                type="button"
                role="radio"
                aria-checked={!contactRequested}
                aria-pressed={!contactRequested}
                onClick={() => {
                  setContactRequested(false)
                  handleFieldChange()
                }}
                className={`checkin-toggle-btn ${!contactRequested ? 'selected-default' : ''}`}
              >
                No requiero contacto
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={contactRequested}
                aria-pressed={contactRequested}
                onClick={() => {
                  setContactRequested(true)
                  handleFieldChange()
                }}
                className={`checkin-toggle-btn ${contactRequested ? 'selected-danger' : ''}`}
              >
                Sí, solicitar contacto
              </button>
            </div>
          </div>

          {contactRequested && (
            <div className="checkin-subpanel checkin-subpanel-blue">
              <label
                htmlFor="contact-comment"
                style={{
                  display: 'block',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  color: '#1e3a8a',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                  marginBottom: '0.45rem',
                }}
              >
                Motivo de contacto o comentario para el médico (opcional)
              </label>
              <textarea
                id="contact-comment"
                rows={2}
                value={contactComment}
                onChange={(e) => {
                  setContactComment(e.target.value)
                  handleFieldChange()
                }}
                placeholder="Indica brevemente el motivo para que el médico prepare la revisión..."
                style={{
                  width: '100%',
                  padding: '0.65rem 0.85rem',
                  fontSize: '0.88rem',
                  color: 'var(--text-main)',
                  backgroundColor: '#ffffff',
                  border: '1px solid #bfdbfe',
                  borderRadius: 'var(--radius-md)',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          )}
        </section>
      </div>
    </Modal>
  )
}
